<!--
  第三方平台类目下拉选择器（单平台）- 基于 vue-virtual-scroller 手搓多列级联面板

  功能说明：
  - 接收 platform prop 决定加载哪个 JSON（bol/otto/kaufland）
  - 三个平台数据统一 normalize → 扁平缓存 → 树形 options
  - 多列级联面板：点击父节点向右展开下一列，点击叶子节点选中并关闭
  - 每列使用 RecycleScroller 虚拟滚动，支持 bol 5000+ 数据不卡顿
  - 模糊搜索：输入关键字后切换为单列结果，展示完整路径
  - 回显：value 非空时自动展开路径并显示类目名
  - 面板 append-to-body + fixed 定位，自动检测视口边界并翻转

  Props：
    platform    {String}   必填，平台代码：'bol' | 'otto' | 'kaufland'
    value       {Array}    类目 ID 路径数组，例如 ['1', '12', '123']
    disabled    {Boolean}  是否禁用
    placeholder {String}   占位文字
    itemSize    {Number}   每行高度，默认 36

  Events：
    change        选中叶子节点时触发，参数：id 路径数组
    load-start    开始加载 JSON 时触发，参数：platform
    load-complete JSON 加载完成时触发，参数：platform
    load-error    加载失败时触发，参数：(platform, error)

  Methods（组件 ref 调用）：
    getCategoryPathDetail()  返回当前 value 对应的各级类目详情

  静态方法（import 组件后调用）：
    PlatformCategorySelect.getCategoryById(platform, categoryId)
    PlatformCategorySelect.getCategoryInfo(platform, categoryIds)
    PlatformCategorySelect.getSubCategories(platform, parentId)  // 获取子分类，不传 parentId 返回根分类
    PlatformCategorySelect.clearCache()
    PlatformCategorySelect.preload('bol', 'otto', 'kaufland')

  使用示例：
    <PlatformCategorySelect
      v-model="categoryPath"
      platform="otto"
      placeholder="请选择OTTO平台对应类目"
      @load-complete="handleLoadComplete"
    />
-->
<template>
  <div v-click-outside="closeDropdown" class="platform-category-select">
    <!-- 输入框 -->
    <div
      class="pcs-input"
      :class="{ 'pcs-input-disabled': disabled }"
      @click="toggleDropdown"
    >
      <span v-if="selectedLabels.length" class="pcs-input-text">
        {{ selectedLabels.join(' / ') }}
      </span>
      <span v-else class="pcs-input-placeholder">{{ placeholder }}</span>
      <Icon
        v-if="clearable && selectedLabels.length && !disabled"
        type="ios-close-circle"
        class="pcs-clear"
        @click.native.stop="handleClear"
      />
      <Icon type="ios-arrow-down" :class="['pcs-arrow', { 'pcs-arrow-open': dropdownVisible }]" />
    </div>

    <!-- 下拉面板 -->
    <div
      v-show="dropdownVisible"
      ref="dropdown"
      class="pcs-dropdown"
      :style="dropdownStyle"
    >
      <!-- 搜索框 -->
      <Input
        v-model="searchKeyword"
        placeholder="试试搜索：指南"
        prefix="ios-search"
        size="small"
        class="pcs-search"
        @input="handleSearchInput"
      />

      <!-- 多列级联面板 -->
      <div v-if="!searchMode" class="pcs-columns">
        <div
          v-for="(column, colIndex) in visibleColumns"
          :key="colIndex"
          class="pcs-column"
        >
          <RecycleScroller
            class="pcs-scroller"
            :items="column"
            :item-size="itemSize"
            key-field="id"
          >
            <template v-slot="{ item }">
              <div
                class="pcs-node"
                :class="{ 'pcs-node-active': isSelected(item, colIndex) }"
                @click="handleNodeClick(item, colIndex)"
              >
                <span class="pcs-node-label">{{ item.label }}</span>
                <Icon
                  v-if="item.hasChildren"
                  type="ios-arrow-forward"
                  class="pcs-node-arrow"
                />
              </div>
            </template>
          </RecycleScroller>
        </div>
      </div>

      <!-- 搜索模式：单列结果 -->
      <div v-else class="pcs-search-result">
        <div class="pcs-search-result-inner">
          <RecycleScroller
            class="pcs-scroller"
            :items="searchResultList"
            :item-size="itemSize"
            key-field="id"
          >
            <template v-slot="{ item }">
              <div class="pcs-node" @click="handleSearchResultClick(item)">
                <span class="pcs-node-label">{{ item.pathLabel }}</span>
              </div>
            </template>
          </RecycleScroller>
        </div>
      </div>

      <div v-if="isEmpty" class="pcs-empty">暂无匹配数据</div>
    </div>
  </div>
</template>

<script>
import { RecycleScroller } from 'vue-virtual-scroller';

// ========== 模块级：数据缓存与工具函数 ==========

const PLATFORM_FILE_MAP = {
  bol: 'bol.json',
  otto: 'otto.json',
  kaufland: 'real.json'
};

const FLAT_DATA_CACHE = {};

// 节流函数，用于限制函数调用的频率
/**
 * 节流函数，用于限制函数调用的频率
 * @param {Function} fn - 需要节流的函数
 * @param {Number} delay - 节流时间间隔，默认 300ms
 * @returns {Function}
 */
function debounce (fn, delay = 300) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * 数据标准化：不同平台 JSON 结构不同，统一转为扁平数组
 * 输出统一格式：[{ id, platformId, categoryName, parentCategoryName }]
 */
function normalizePlatformData (rawDatas) {
  if (!rawDatas || !rawDatas.length) return [];
  const isNested = rawDatas.some(
    item => item.platformCategoryVOList && item.platformCategoryVOList.length > 0
  );
  if (!isNested) {
    return rawDatas.map(item => ({
      id: String(item.id),
      platformId: item.platformId,
      categoryName: item.categoryName,
      parentCategoryName: item.parentCategoryName || null
    }));
  }
  const result = [];
  function walk (nodes) {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      result.push({
        id: String(node.id),
        platformId: node.platformId,
        categoryName: node.categoryName,
        parentCategoryName: node.parentCategoryName || null
      });
      if (node.platformCategoryVOList && node.platformCategoryVOList.length) {
        walk(node.platformCategoryVOList);
      }
    }
  }
  walk(rawDatas);
  return result;
}

// 加载平台类目扁平数据（缓存），并进行数据标准化处理
async function loadPlatformFlatData (platformCode) {
  if (FLAT_DATA_CACHE[platformCode]) return FLAT_DATA_CACHE[platformCode];
  const fileName = PLATFORM_FILE_MAP[platformCode] || `${platformCode}.json`;
  const module = require('./staticData/' + fileName);
  const rawDatas = (module && module.datas) || (module && module.default && module.default.datas) || [];
  const flatData = normalizePlatformData(rawDatas);
  FLAT_DATA_CACHE[platformCode] = flatData;
  return flatData;
}

// 根据扁平数据 + 分类 ID 构造完整分类信息（静态方法用）
function buildCategoryInfo (flatData, categoryId) {
  const targetId = String(categoryId);
  const node = flatData.find(item => String(item.id) === targetId);
  if (!node) return null;
  const nameMap = {};
  flatData.forEach(item => { nameMap[item.categoryName] = item; });
  const ancestors = [];
  let current = node;
  let level = 1;
  while (current && current.parentCategoryName && nameMap[current.parentCategoryName]) {
    const parent = nameMap[current.parentCategoryName];
    ancestors.unshift({ id: String(parent.id), categoryName: parent.categoryName });
    current = parent;
    level++;
  }
  return {
    id: String(node.id),
    platformId: node.platformId,
    categoryName: node.categoryName,
    parentCategoryName: node.parentCategoryName,
    parentCategoryId: ancestors.length ? ancestors[ancestors.length - 1].id : null,
    level,
    ancestors
  };
}

// 获取某分类下的直接子分类（不传 parentId 则返回根分类）
async function getSubCategories (platform, parentId) {
  const flatData = await loadPlatformFlatData(platform);
  if (!parentId) {
    const nameMap = {};
    flatData.forEach(item => { nameMap[item.categoryName] = item; });
    return flatData.filter(item => !item.parentCategoryName || !nameMap[item.parentCategoryName]);
  }
  const parent = flatData.find(item => String(item.id) === String(parentId));
  if (!parent) return [];
  return flatData.filter(item => item.parentCategoryName === parent.categoryName);
}

// 点击外部指令（面板 append-to-body 后需同时判断 body 中的面板）
const clickOutside = {
  bind (el, binding) {
    el._clickOutside = function (e) {
      const dropdown = el._pcsDropdownEl;
      const isInside = el.contains(e.target) || (dropdown && dropdown.contains(e.target));
      if (!isInside) {
        binding.value();
      }
    };
    document.addEventListener('click', el._clickOutside, true);
  },
  unbind (el) {
    document.removeEventListener('click', el._clickOutside, true);
    el._clickOutside = null;
    el._pcsDropdownEl = null;
  }
};

// ========== 组件定义 ==========

const componentOptions = {
  name: 'PlatformCategorySelect',
  components: { RecycleScroller },
  directives: { 'click-outside': clickOutside },
  model: { prop: 'value', event: 'change' },
  props: {
    platform: { type: String, required: true },
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: '请选择' },
    itemSize: { type: Number, default: 36 },
    clearable: { type: Boolean, default: true }
  },
  data () {
    return {
      dropdownVisible: false,
      dropdownStyle: {},
      columnWidth: 240,
      searchKeyword: '',
      searchMode: false,
      treeData: [],
      selectedPath: [],
      selectedLabels: [],
      loaded: false,
      inited: false
    };
  },
  computed: {
    visibleColumns () {
      // 根据 selectedPath 动态生成多列：第 0 列为根节点，
      // 第 i+1 列为 selectedPath[i] 的子节点
      if (!this.treeData || !this.treeData.length) return [];
      const columns = [];
      columns.push(this.treeData);
      let currentList = this.treeData;
      for (let i = 0; i < this.selectedPath.length; i++) {
        const selectedId = this.selectedPath[i];
        const selectedNode = currentList.find(n => n.id === selectedId);
        if (selectedNode && selectedNode.children && selectedNode.children.length) {
          columns.push(selectedNode.children);
          currentList = selectedNode.children;
        } else {
          break;
        }
      }
      return columns;
    },
    searchResultList () {
      const keyword = this.searchKeyword.trim().toLowerCase();
      if (!keyword || !this.treeData.length) return [];
      const result = [];
      const walk = (nodes, path) => {
        for (const node of nodes) {
          const currentPath = path.concat(node);
          if (node.label.toLowerCase().includes(keyword)) {
            result.push({
              id: node.id,
              label: node.label,
              pathLabel: currentPath.map(n => n.label).join(' / '),
              node,
              path: currentPath
            });
          }
          if (node.children && node.children.length) {
            walk(node.children, currentPath);
          }
        }
      };
      walk(this.treeData, []);
      return result;
    },
    isEmpty () {
      if (this.searchMode) return this.searchResultList.length === 0;
      return this.visibleColumns.length === 0;
    }
  },
  watch: {
    platform () {
      this.reset();
      this.init();
    },
    value: {
      immediate: true,
      handler (val) {
        this.syncValue(val);
      }
    }
  },
  created () {
    this.init();
  },
  methods: {
    // ==================== 初始化 ====================

    reset () {
      this.loaded = false;
      this.inited = false;
      this.treeData = [];
      this.selectedPath = [];
      this.selectedLabels = [];
      this.searchKeyword = '';
      this.searchMode = false;
    },

    async init () {
      if (this.loading) return;
      this.loading = true;
      try {
        this.$emit('load-start', this.platform);
        const flatData = await loadPlatformFlatData(this.platform);

        this.flatData = flatData;
        this.idMap = {};
        flatData.forEach(item => { this.idMap[String(item.id)] = item; });
        this.nameMap = {};
        flatData.forEach(item => { this.nameMap[item.categoryName] = item; });

        const tree = this.buildTree(flatData);
        this.treeData = tree;
        this.loaded = true;

        // eslint-disable-next-line no-console
        // console.log(`[PCat:${this.platform}] flatData: ${flatData.length}, treeNodes: ${tree.length}`);

        this.$emit('load-complete', this.platform);

        // 回显
        if (this.value && this.value.length) {
          this.syncValue(this.value);
          this.inited = true;
        }
      } catch (err) {
        this.$emit('load-error', this.platform, err);
        // eslint-disable-next-line no-console
        console.error(`[PlatformCategorySelect] 加载 ${this.platform} 类目数据失败`, err);
      } finally {
        this.loading = false;
      }
    },

    // ==================== 树形数据构建 ====================

    buildTree (flatData) {
      const childrenMap = {};
      flatData.forEach(item => {
        if (!item.parentCategoryName) return;
        if (!childrenMap[item.parentCategoryName]) {
          childrenMap[item.parentCategoryName] = [];
        }
        childrenMap[item.parentCategoryName].push(item);
      });
      const nameSet = new Set();
      flatData.forEach(item => { nameSet.add(item.categoryName); });
      const roots = [];
      flatData.forEach(item => {
        if (!item.parentCategoryName || !nameSet.has(item.parentCategoryName)) {
          roots.push(this.buildNode(item, childrenMap));
        }
      });
      return roots;
    },

    buildNode (item, childrenMap) {
      const node = {
        id: String(item.id),
        label: item.categoryName,
        value: String(item.id),
        hasChildren: false,
        children: undefined,
        raw: item
      };
      const children = childrenMap[item.categoryName];
      if (children && children.length) {
        node.hasChildren = true;
        node.children = children.map(child => this.buildNode(child, childrenMap));
      }
      return node;
    },

    // ==================== 值同步 ====================

    syncValue (val) {
      if (!val || !val.length) {
        this.selectedPath = [];
        this.selectedLabels = [];
        return;
      }
      // 根据 value（id 数组）反推路径
      const path = [];
      const labels = [];
      let currentList = this.treeData;
      for (let i = 0; i < val.length; i++) {
        const node = currentList.find(n => n.id === String(val[i]));
        if (!node) break;
        path.push(node.id);
        labels.push(node.label);
        currentList = node.children || [];
      }
      this.selectedPath = path;
      this.selectedLabels = labels;
    },

    // ==================== 交互 ====================

    toggleDropdown () {
      if (this.disabled) return;
      if (this.dropdownVisible) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },

    openDropdown () {
      this.dropdownVisible = true;
      this.$nextTick(() => {
        this.appendDropdownToBody();
        this.updateDropdownPosition();
        this.bindPositionEvents();
      });
    },

    closeDropdown () {
      if (!this.dropdownVisible) return;
      this.dropdownVisible = false;
      this.searchKeyword = '';
      this.searchMode = false;
      this.unbindPositionEvents();
      this.restoreDropdown();
    },

    // ==================== append-to-body + 自动翻转定位 ====================

    getDropdownEl () {
      return this.$refs.dropdown || null;
    },

    appendDropdownToBody () {
      const dropdown = this.getDropdownEl();
      if (!dropdown || dropdown.parentNode === document.body) return;
      this._dropdownPlaceholder = document.createComment('pcs-dropdown-placeholder');
      dropdown.parentNode.replaceChild(this._dropdownPlaceholder, dropdown);
      document.body.appendChild(dropdown);
      this.$el._pcsDropdownEl = dropdown;
    },

    restoreDropdown () {
      const dropdown = this.getDropdownEl();
      if (!dropdown || dropdown.parentNode !== document.body) return;
      if (this._dropdownPlaceholder && this._dropdownPlaceholder.parentNode) {
        this._dropdownPlaceholder.parentNode.replaceChild(dropdown, this._dropdownPlaceholder);
        this._dropdownPlaceholder = null;
      } else {
        document.body.removeChild(dropdown);
      }
      this.$el._pcsDropdownEl = null;
    },

    bindPositionEvents () {
      this._onReposition = () => this.updateDropdownPosition();
      window.addEventListener('resize', this._onReposition);
      window.addEventListener('scroll', this._onReposition, true);
    },

    unbindPositionEvents () {
      if (this._onReposition) {
        window.removeEventListener('resize', this._onReposition);
        window.removeEventListener('scroll', this._onReposition, true);
        this._onReposition = null;
      }
    },

    updateDropdownPosition () {
      if (!this.dropdownVisible) return;
      const inputEl = this.$el;
      const dropdown = this.getDropdownEl();
      if (!inputEl || !dropdown) return;

      const inputRect = inputEl.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const margin = 8;

      // 面板宽度：按已展开列数逐级增加，至少一列，不超过视口
      const columnCount = Math.max(1, this.visibleColumns.length);
      let dropdownW = this.columnWidth * columnCount;
      const maxW = Math.max(this.columnWidth, viewportW - margin * 2);
      dropdownW = Math.min(dropdownW, maxW);

      // 面板高度：取当前渲染后的实际高度，未渲染时用默认值
      const dropdownH = dropdown.offsetHeight || 366;

      // 水平定位：默认与输入框左对齐；右侧空间不足时靠右
      let left = inputRect.left;
      if (left + dropdownW > viewportW - margin) {
        left = Math.max(margin, viewportW - dropdownW - margin);
      }

      // 垂直定位：默认在输入框下方；下方空间不足时翻转到上方
      let top = inputRect.bottom + margin;
      let placement = 'bottom';
      if (top + dropdownH > viewportH - margin) {
        const topAbove = inputRect.top - dropdownH - margin;
        if (topAbove >= margin) {
          top = topAbove;
          placement = 'top';
        }
      }

      this.dropdownStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${dropdownW}px`,
        zIndex: 10000
      };

      this._dropdownPlacement = placement;
    },

    isSelected (node, colIndex) {
      return this.selectedPath[colIndex] === node.id;
    },

    handleNodeClick (node, colIndex) {
      const newPath = this.selectedPath.slice(0, colIndex).concat(node.id);
      this.selectedPath = newPath;
      this.selectedLabels = this.getPathLabels(newPath);
      this.$nextTick(() => {
        this.updateDropdownPosition();
      });
      if (!node.hasChildren) {
        // 点击叶子节点：选中并关闭
        this.$emit('change', [...newPath]);
        this.dropdownVisible = false;
        this.searchKeyword = '';
        this.searchMode = false;
      }
    },

    getPathLabels (path) {
      const labels = [];
      let currentList = this.treeData;
      for (const id of path) {
        const node = currentList.find(n => n.id === id);
        if (!node) break;
        labels.push(node.label);
        currentList = node.children || [];
      }
      return labels;
    },

    // ==================== 搜索 ====================

    handleSearchInput: debounce(function () {
      this.searchMode = this.searchKeyword.trim().length > 0;
      this.$nextTick(() => {
        this.updateDropdownPosition();
      });
    }, 300),

    handleSearchResultClick (item) {
      const path = item.path.map(n => n.id);
      this.selectedPath = path;
      this.selectedLabels = item.path.map(n => n.label);
      this.$emit('change', [...path]);
      this.dropdownVisible = false;
      this.searchKeyword = '';
      this.searchMode = false;
    },

    // ==================== 清除 ====================

    handleClear () {
      this.selectedPath = [];
      this.selectedLabels = [];
      this.$emit('change', []);
      this.$emit('clear');
    },

    // ==================== 对外暴露 ====================

    getCategoryPathDetail () {
      const value = this.value || [];
      const detail = [];
      for (let i = 0; i < value.length; i++) {
        const item = this.idMap ? this.idMap[String(value[i])] : null;
        if (!item) break;
        detail.push({
          categoryLevel: i + 1,
          categoryId: String(item.id),
          categoryName: item.categoryName,
          parentCategoryId: i === 0 ? null : String(value[i - 1])
        });
      }
      return detail;
    }
  },
  beforeDestroy () {
    this.unbindPositionEvents();
    this.restoreDropdown();
  }
};

// ========== 静态方法（可在组件外部直接调用） ==========

/**
 * 根据平台 + 类目 ID 查询单个类目详情（含层级信息）
 * 返回：{ id, platformId, categoryName, parentCategoryName, parentCategoryId, level, ancestors }
 */
componentOptions.getCategoryById = async function (platform, categoryId) {
  const flatData = await loadPlatformFlatData(platform);
  return buildCategoryInfo(flatData, categoryId);
};

/**
 * 批量查询类目详情
 */
componentOptions.getCategoryInfo = async function (platform, categoryIds) {
  const ids = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
  const flatData = await loadPlatformFlatData(platform);
  return ids.map(id => buildCategoryInfo(flatData, id)).filter(Boolean);
};

/**
 * 清空所有平台类目数据缓存
 */
componentOptions.clearCache = function () {
  Object.keys(FLAT_DATA_CACHE).forEach(key => delete FLAT_DATA_CACHE[key]);
};

/**
 * 预加载指定平台数据（不传参数则预加载全部）
 */
componentOptions.preload = async function (...platforms) {
  const list = platforms.length ? platforms : Object.keys(PLATFORM_FILE_MAP);
  await Promise.all(list.map(code => loadPlatformFlatData(code)));
};

/**
 * 获取某分类下的直接子分类（不传 parentId 则返回根分类）
 * 返回：[{ id, platformId, categoryName, parentCategoryName }]
 */
componentOptions.getSubCategories = async function (platform, parentId) {
  return getSubCategories(platform, parentId);
};

export default componentOptions;
</script>

<style>
.platform-category-select {
  position: relative;
  width: 100%;
}

.pcs-input {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;
}

.pcs-input:hover {
  border-color: #57a3f3;
}

.pcs-input-disabled {
  background: #f3f3f3;
  cursor: not-allowed;
}

.pcs-input-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  color: #515a6e;
}

.pcs-input-placeholder {
  flex: 1;
  color: #c5c8ce;
  font-size: 14px;
}

.pcs-arrow {
  margin-left: 8px;
  font-size: 14px;
  color: #808695;
  transition: transform 0.2s;
}

.pcs-arrow-open {
  transform: rotate(180deg);
}

.pcs-clear {
  margin-left: 4px;
  font-size: 16px;
  color: #c5c8ce;
  cursor: pointer;
  transition: color 0.2s;
}

.pcs-clear:hover {
  color: #515a6e;
}

.pcs-dropdown {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000;
  min-width: 240px;
  max-width: 100vw;
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.pcs-search {
  padding: 8px 8px 0;
}
.pcs-search .ivu-input-prefix {
  height: 0;
  top: 25%;
  left: 8px;
}
.pcs-search .ivu-input-small {
  border-radius: 4px;
}

.pcs-columns {
  display: flex;
  max-height: 320px;
  padding: 8px;
  overflow: hidden;
}

.pcs-column {
  width: 240px;
  min-width: 240px;
  border: 1px solid #e8eaec;
  border-right: none;
  overflow: hidden;
  padding-right:10px;
}

.pcs-column:first-child {
  border-radius: 4px 0 0 4px;
}

.pcs-column:last-child {
  border-right: 1px solid #e8eaec;
  border-radius: 0 4px 4px 0;
}

.pcs-column:only-child {
  border-radius: 4px;
  border-right: 1px solid #e8eaec;
}

.pcs-scroller {
  height: 320px;
  padding-bottom:20px;
}

.pcs-search-result {
  padding: 8px;
}

.pcs-search-result-inner {
  border: 1px solid #e8eaec;
  border-radius: 4px;
  overflow: hidden;
}

.pcs-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 12px;
  cursor: pointer;
  transition: background 0.15s;
}

.pcs-node:hover {
  background: #f3f3f3;
}

.pcs-node-active {
  background: #e6f7ff;
  color: #2d8cf0;
}

.pcs-node-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.pcs-node-arrow {
  margin-left: 8px;
  font-size: 12px;
  color: #808695;
}

.pcs-empty {
  padding: 0 8px 16px;
  text-align: center;
  color: #c5c8ce;
  font-size: 14px;
}
</style>
